import { Injectable } from '@angular/core';
import {VaccineEvent} from '../../shared/interfaces/vaccine-event';
import {KeishaHibetsuFile, KyuujiRyouForGraph, KyuusuiRyouForGraph} from '../../shared/interfaces/keisha-hibetsu-file';
import {ShiyoulotV2} from '../../shared/interfaces/shiyoulot';
import {CommonService} from './common.service';
import {MasterService} from './master.service';
import {KeishaHibetsuFileService} from './keisha-hibetsu-file.service';
import {TaisokuEvent} from '../../shared/interfaces/taisoku-event';
import {HEISHI_TOUTA_PROPERTIES, KEISHA_SHUBETSU} from '../../shared/constants';
import {ShiyoulotHibetsuFile} from '../../shared/interfaces/shiyoulot-hibetsu-file';
import {ShukkaSaki} from '../../shared/interfaces/shukkasaki';
import {Keishu} from '../../shared/interfaces/keishu';
import {Keisha} from '../../shared/interfaces/keisha';

/**
 * 飼養ロットCSVの行データのinterface
 */
export interface ShiyoulotInfoCSVRow {
  key: string;
  averageWeight: string;
  ikuseiRitsu: number;
  genzaiWasuu: number;
  minTemperature: string;
  highTemperature: string;
  kyuujiRyou: number;
  kyuusuiRyou: number;
  osuWasuu: number;
  ashiWasuu: number;
  kuchiWasuu: number;
  kikeiWasuu: number;
  shouWasuu: number;
  otherWasuu: number;
  heishiWasuu: number;
  health: string;
  tentouJikan: string;
  feed: string;
  operation: string;
}

/**
 * 体測CSVの行データのinterface
 */
export interface TaisokuCSVRow {
  key: string;
  jisshiNichirei: string;
  mean: string;
  cv: string;
}

/**
 * ワクチンCSVの行データのinterface
 */
export interface VaccineCSVRow {
  farmName: string;
  shukkaSakiName: string;
  edukeDate: string;
  edukeWasuu: number;
  keishu: string;
  vaccineName: string;
  vaccineKabu: number;
  maker: string;
  vaccineLotNumber: string;
  yuukouKigen: string;
  jisshiDate: string;
  jisshiNichirei: string;
  health: string;
  touyoHouhou: string;
  youkaiEki: string;
  dansuiHour: string;
  youkaiGoJikan: string;
  youkaiRyou: number;
  isWashedNyuukon: boolean;
  sesshuBui: string;
  isHattou: boolean;
  changeNeedleFrequency: string;
  isWashedPister: boolean;
}

interface ShiyoulotIchiranCSV {
  farmKeisha: string;
  shiyoulotNumber: string;
  edukeDate: string;
  dounyuuDate: string;
  nichirei: number | string;
  keishu: string;
  shukkaSakiName: string;
  genzaiWasuu: number;
  nyuukaWasuu: number;
  ikuseiRitsu: string;
}

@Injectable({
  providedIn: 'root'
})
export class CsvUtilityService {

  constructor(private commonService: CommonService,
              private masterService: MasterService,
              private keishaHibetsuFileService: KeishaHibetsuFileService,
              ) { }

  /**
   * 1鶏舎分の作業記録CSVの行データを生成
   * csvの1行分のデータは複数の鶏舎の作業記録を含む
   *
   * @param keishaId
   * @param keishaHibetsuFile
   */
  genSagyouKirokuForCSVRow(keishaId: string, keishaHibetsuFile?: KeishaHibetsuFile) {
    const operatorPropName = 'operator_' + keishaId;
    const operationPropName = 'operation_' + keishaId;
    const operators = keishaHibetsuFile?.operatorList
      .filter(r => r.worked)
      .map(r => r.name) ?? [];
    const joinedOperators = operators.join(' ');
    const operation = keishaHibetsuFile?.operation ?? '';
    return {
      [operatorPropName]: joinedOperators,
      [operationPropName]: operation,
    };
  }

  /**
   * 作業記録CSVのヘッダ情報を生成
   *
   * @param keishaIdList
   */
  genSagyouKirokuPropertiesForCSV(keishaIdList: string[]) {
    const header: { prop: string; name: string; }[] = [{prop: 'key', name: '日付'}];
    keishaIdList.forEach(keishaId => {
      const keishaName = this.commonService.getName(this.masterService.keishaMaster, keishaId);
      const operatorPropName = 'operator_' + keishaId;
      const operationPropName = 'operation_' + keishaId;
      header.push(
        {prop: operatorPropName, name: '作業者_' + keishaName},
        {prop: operationPropName, name: '作業内容_' + keishaName},
      );
    });
    return header;
  }

  /**
   * 飼養ロット情報CSVの行データを生成
   *
   * @param shiyoulotHibetsuFile
   * @param keishaHibetsuFiles
   * @param keisokuZumiTaisokuEvents
   * @param kyuujiRyous
   * @param kyuusuiRyous
   * @param shiyoulot
   */
  genShiyoulotInfoCSVRow(shiyoulotHibetsuFile: ShiyoulotHibetsuFile,
                         keishaHibetsuFiles: KeishaHibetsuFile[],
                         keisokuZumiTaisokuEvents: TaisokuEvent[],
                         kyuujiRyous: KyuujiRyouForGraph[],
                         kyuusuiRyous: KyuusuiRyouForGraph[],
                         shiyoulot?: ShiyoulotV2) {
    const key = shiyoulotHibetsuFile.id;
    const averageWeight = keisokuZumiTaisokuEvents.find((taisokuEvent: TaisokuEvent) => taisokuEvent.jisshiDate === key)?.mean ?? '';
    const allNyuukaWasuu = shiyoulot?.allNyuukaWasuu ?? 0;
    const keishaHibetsuFile = keishaHibetsuFiles.find((keishaHibetsuFile: KeishaHibetsuFile) => keishaHibetsuFile.id === key);
    const genzaiWasuu = this.commonService.calcGenzaiWasuuForShiyoulotHibetsuFile(shiyoulotHibetsuFile);
    const ikuseiRitsu = allNyuukaWasuu > 0
      ? Math.round(genzaiWasuu / allNyuukaWasuu * 10000) / 100
      : 0;
    const minTemperature = keishaHibetsuFile?.temperature
      ? this.keishaHibetsuFileService.getLowestTemperatureValue(keishaHibetsuFile.temperature)
      : '';
    const highTemperature = keishaHibetsuFile?.temperature
      ? this.keishaHibetsuFileService.getHighestTemperatureValue(keishaHibetsuFile.temperature)
      : '';
    const kyuujiRyou = kyuujiRyous.find((kyuujiRyou: KyuujiRyouForGraph) => kyuujiRyou.id === key)?.kyuujiRyou ?? '';
    const kyuusuiRyou = kyuusuiRyous.find((kyuusuiRyou: KyuusuiRyouForGraph) => kyuusuiRyou.id === key)?.kyuusuiRyou ?? '';
    const osuWasuu = shiyoulotHibetsuFile?.osuWasuu ?? '';
    const ashiWasuu = shiyoulotHibetsuFile?.ashiWasuu ?? '';
    const kuchiWasuu = shiyoulotHibetsuFile?.kuchiWasuu ?? '';
    const kikeiWasuu = shiyoulotHibetsuFile?.kikeiWasuu ?? '';
    const shouWasuu = shiyoulotHibetsuFile?.shouWasuu ?? '';
    const otherWasuu = shiyoulotHibetsuFile?.otherWasuu ?? '';
    const heishiWasuu = shiyoulotHibetsuFile?.heishiWasuu ?? '';
    const health = this.keishaHibetsuFileService.getAbnormal(keishaHibetsuFile);
    const tentouJikan = keishaHibetsuFile?.tentouShoutouJikoku
      ? keishaHibetsuFile?.tentouShoutouJikoku[0].start + '~' + keishaHibetsuFile?.tentouShoutouJikoku[0].end
      : '';
    const feed = keishaHibetsuFile?.feed?.feedShubetsu ?? ''
    const operation = keishaHibetsuFile?.operation ?? '';
    return {
      key: key,
      averageWeight: averageWeight,
      ikuseiRitsu: ikuseiRitsu,
      genzaiWasuu: genzaiWasuu,
      minTemperature: this.commonService.emptyWhenFalse(minTemperature),
      highTemperature: this.commonService.emptyWhenFalse(highTemperature),
      kyuujiRyou: kyuujiRyou,
      kyuusuiRyou: kyuusuiRyou,
      osuWasuu: osuWasuu,
      ashiWasuu: ashiWasuu,
      kuchiWasuu: kuchiWasuu,
      kikeiWasuu: kikeiWasuu,
      shouWasuu: shouWasuu,
      otherWasuu: otherWasuu,
      heishiWasuu: heishiWasuu,
      health: health,
      tentouJikan: tentouJikan,
      feed: feed,
      operation: operation,
    } as ShiyoulotInfoCSVRow;
  }

  /**
   * 飼養ロット情報CSVのヘッダ情報を生成
   * @private
   */
  genShiyoulotInfoPropertiesForCSV() {
    return [
      {prop: 'key', name: '日付'},
      {prop: 'averageWeight', name: '体重(g)'}, // 平均体重
      {prop: 'ikuseiRitsu', name: '育成率(%)'}, // 育成率
      {prop: 'genzaiWasuu', name: '現在羽数'}, // 現在羽数
      {prop: 'minTemperature', name: '最低気温'}, // 最低気温
      {prop: 'highTemperature', name: '最高気温'}, // 最高気温
      {prop: 'kyuujiRyou', name: '給餌量(g/羽)'}, // 給餌量
      {prop: 'kyuusuiRyou', name: '給水量(ml/羽)'}, // 給水量
      {prop: 'osuWasuu', name: HEISHI_TOUTA_PROPERTIES.osuWasuu}, // ♂混ざり・誤鑑
      {prop: 'ashiWasuu', name: HEISHI_TOUTA_PROPERTIES.ashiWasuu}, // 脚弱・脚折れ
      {prop: 'kuchiWasuu', name: HEISHI_TOUTA_PROPERTIES.kuchiWasuu}, // 口曲がり
      {prop: 'kikeiWasuu', name: HEISHI_TOUTA_PROPERTIES.kikeiWasuu}, // 奇形
      {prop: 'shouWasuu', name: HEISHI_TOUTA_PROPERTIES.shouWasuu}, // 未熟・小さい個体
      {prop: 'otherWasuu', name: HEISHI_TOUTA_PROPERTIES.otherWasuu}, // その他淘汰
      {prop: 'heishiWasuu', name: HEISHI_TOUTA_PROPERTIES.heishiWasuu}, // 斃死
      {prop: 'health', name: '家禽の健康状態'},
      {prop: 'tentouJikan', name: '点灯時間'},
      {prop: 'feed', name: '飼料の種類'},
      {prop: 'operation', name: '作業内容'},
    ];
  }

  /**
   * 体測CSVの行データを生成
   *
   * @param taisokuEvent
   */
  genTaisokuCSVRow(taisokuEvent: TaisokuEvent) {
    const jisshiNichirei = !!taisokuEvent.jisshiDate
      ? this.commonService.calcNichirei(taisokuEvent.taishouEdukeDate, taisokuEvent.jisshiDate)
      : '';
    return {
      key: taisokuEvent.jisshiDate,
      jisshiNichirei: jisshiNichirei,
      mean: taisokuEvent.mean ?? '',
      cv: taisokuEvent.cv ?? '',
    } as TaisokuCSVRow;
  }

  genTaisokuPropertiesForCSV() {
    return [
      {prop: 'key', name: '実施日'},
      {prop: 'jisshiNichirei', name: '日齢'},
      {prop: 'mean', name: '平均体重(g)'},
      {prop: 'cv', name: 'cv'}
    ];
  }

  /**
   * ワクチンCSVの行データを生成
   *
   * @param vaccineEvent
   * @private
   */
  genVaccineCSVRow(vaccineEvent: VaccineEvent,
                   keishaHibetsuFileList: KeishaHibetsuFile[],
                   shiyoulot?: ShiyoulotV2,
                   shukkaSakiId?: string) {
    const jisshiNichirei = vaccineEvent.jisshiDate
      ? this.commonService.calcNichirei(vaccineEvent.taishouEdukeDate, vaccineEvent.jisshiDate)
      : '';
    const keishaHibetsuFile = keishaHibetsuFileList
      .find(keishaHibetsuFile => keishaHibetsuFile.id === vaccineEvent.jisshiDate);
    return {
      farmName: this.commonService.emptyWhenFalse(this.commonService.getName(this.masterService.keishaMaster, vaccineEvent.taishouKeisha, 'farmName')),
      shukkaSakiName: this.commonService.emptyWhenFalse(this.commonService.getName(this.masterService.shukkaSakiMaster, shukkaSakiId)),
      edukeDate: this.commonService.emptyWhenFalse(shiyoulot ? shiyoulot.edukeDate : ''),
      edukeWasuu: this.commonService.emptyWhenFalse(shiyoulot ? shiyoulot.allNyuukaWasuu : ''),
      keishu: this.commonService.emptyWhenFalse(this.commonService.getName(this.masterService.keishuMaster, vaccineEvent.taishouKeishu)),
      vaccineName: this.commonService.emptyWhenFalse(vaccineEvent.vaccineName),
      vaccineKabu: this.commonService.emptyWhenFalse(vaccineEvent.vaccineKabu),
      maker: this.commonService.emptyWhenFalse(vaccineEvent.maker),
      vaccineLotNumber: this.commonService.emptyWhenFalse(vaccineEvent.vaccineLotNumber),
      yuukouKigen: this.commonService.emptyWhenFalse(vaccineEvent.yuukouKigen),
      jisshiDate: this.commonService.emptyWhenFalse(vaccineEvent.jisshiDate),
      jisshiNichirei: this.commonService.emptyWhenFalse(jisshiNichirei),
      health: this.keishaHibetsuFileService.getAbnormal(keishaHibetsuFile),
      touyoHouhou: this.commonService.emptyWhenFalse(vaccineEvent.touyoHouhou),
      youkaiEki: this.commonService.emptyWhenFalse(vaccineEvent.youkaiEki),
      dansuiHour: this.commonService.emptyWhenFalse(vaccineEvent.dansuiHour),
      youkaiGoJikan: this.commonService.emptyWhenFalse(vaccineEvent.youkaiGoJikan),
      youkaiRyou: this.commonService.emptyWhenFalse(vaccineEvent.youkaiRyou),
      isWashedNyuukon: this.commonService.emptyWhenFalse(vaccineEvent.isWashedNyuukon),
      sesshuBui: this.commonService.emptyWhenFalse(vaccineEvent.sesshuBui),
      isHattou: this.commonService.emptyWhenFalse(vaccineEvent.isHattou),
      changeNeedleFrequency: this.commonService.emptyWhenFalse(vaccineEvent.changeNeedleFrequency),
      isWashedPister: this.commonService.emptyWhenFalse(vaccineEvent.isWashedPister),
    } as VaccineCSVRow;
  }

  /**
   * ワクチンCSVのヘッダ情報を生成
   * @private
   */
  genVaccinePropertiesForCSV() {
    return [
      {prop: 'farmName', name: '農場名'},
      {prop: 'shukkaSakiName', name: '顧客名'}, // 出荷先名
      {prop: 'edukeDate', name: '餌付け日'},
      {prop: 'edukeWasuu', name: '餌付け羽数'},
      {prop: 'keishu', name: '鶏種'},
      {prop: 'vaccineName', name: 'ワクチン名'},
      {prop: 'vaccineKabu', name: '株'},
      {prop: 'maker', name: 'メーカー名'},
      {prop: 'vaccineLotNumber', name: 'ワクチンロットNo'},
      {prop: 'yuukouKigen', name: '有効期限'},
      {prop: 'jisshiDate', name: '投与月日'},
      {prop: 'jisshiNichirei', name: '投与日齢'}, // 実施日から計算
      {prop: 'health', name: '雛の健康状態'}, // 鶏舎日別ファイルから取得
      {prop: 'touyoHouhou', name: '投与方法'},
      {prop: 'youkaiEki', name: '溶解液'},
      {prop: 'dansuiHour', name: '断水時間'},
      {prop: 'youkaiGoJikan', name: '溶解後の投与時間'},
      {prop: 'youkaiRyou', name: '溶解量'},
      {prop: 'isWashedNyuukon', name: 'ニューコンの洗浄'},
      {prop: 'sesshuBui', name: '接種部位'},
      {prop: 'isHattou', name: '発症の確認'},
      {prop: 'changeNeedleFrequency', name: '接種針の交換'},
      {prop: 'isWashedPister', name: 'ピスターの洗浄'},
    ];
  }

  /**
   * 飼養ロット一覧CSVの行データを生成
   * @param shiyoulot
   */
  genShiyoulotIchiranCSVRow(shiyoulot: ShiyoulotV2): ShiyoulotIchiranCSV {
    const nichirei = this.commonService.getShiyoulotNichirei(shiyoulot);
    return {
      farmKeisha: this.commonService.getValue<Keisha, string>(this.masterService.keishaMaster, 'id', shiyoulot.keishaId, 'farmKeisha'),
      shiyoulotNumber: shiyoulot.shiyoulotNumber?.toString() ?? '',
      edukeDate: shiyoulot.edukeDate,
      dounyuuDate: this.commonService.getKeishaShubetsu(shiyoulot.keishaId ?? '', this.masterService.keishaMaster) === KEISHA_SHUBETSU.IKUSEI ? shiyoulot.dounyuuDate : "",
      nichirei: nichirei,
      keishu: this.commonService.getValue<Keishu, string>(this.masterService.keishuMaster, 'id', shiyoulot.keishuId, 'name'),
      shukkaSakiName: this.commonService.getValue<ShukkaSaki, string>(this.masterService.shukkaSakiMaster, 'id', shiyoulot.daihyouShukkaSakiId, 'name'),
      genzaiWasuu: this.commonService.calcGenzaiWasuuForShiyoulotFile(shiyoulot),
      nyuukaWasuu: shiyoulot.allNyuukaWasuu,
      ikuseiRitsu: this.commonService.getIkuseiRitsu(shiyoulot) + '%',
    };
  }

  /**
   * 飼養ロット一覧CSVのヘッダ情報を生成
   */
  genShiyoulotIchiranPropertiesForCSV() {
    return [
      {prop: 'farmKeisha', name: '鶏舎名'},
      {prop: 'shiyoulotNumber', name: '飼養ロット番号'},
      {prop: 'edukeDate', name: '餌付け日'},
      {prop: 'dounyuuDate', name: '導入日'},
      {prop: 'nichirei', name: '日齢'},
      {prop: 'keishu', name: '鶏種'},
      {prop: 'shukkaSakiName', name: '出荷先'},
      {prop: 'genzaiWasuu', name: '現在羽数'},
      {prop: 'nyuukaWasuu', name: '入荷羽数'},
      {prop: 'ikuseiRitsu', name: '育成率'},
    ];
  }
}
